import { getExportStatusAPI, downloadPDFAPI, downloadZipAPI, downloadOfficeAPI } from '@/api/h5/export';
import { createExportPollingStorage } from './exportPollingStorage';

const TAB_ID_KEY = 'export-polling-tab-id';
const WINDOW_NAME_PREFIX = 'export-polling-tab-id:';

function getTabId() {
    try {
        // window.name is NOT copied when a tab is opened via window.open (unlike
        // sessionStorage, which is cloned from the opener). So check window.name first:
        // if it already carries our prefix the tab survived a refresh and we reuse the id;
        // otherwise this is a brand-new tab (including one spawned by window.open) and we
        // must generate a fresh id to avoid sharing the opener's id.
        const existingName = (typeof window !== 'undefined' && window.name) || '';
        if (existingName.startsWith(WINDOW_NAME_PREFIX)) {
            return existingName.slice(WINDOW_NAME_PREFIX.length);
        }
        const tabId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
        window.name = `${WINDOW_NAME_PREFIX}${tabId}`;
        try { sessionStorage.setItem(TAB_ID_KEY, tabId); } catch (_) {}
        return tabId;
    } catch (e) {
        try {
            if (typeof window !== 'undefined') {
                const existing = window.name || '';
                if (existing.startsWith(WINDOW_NAME_PREFIX)) {
                    return existing.slice(WINDOW_NAME_PREFIX.length);
                }
                const tabId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
                window.name = `${WINDOW_NAME_PREFIX}${tabId}`;
                return tabId;
            }
        } catch (windowError) {
            // fall through to a process-local fallback
        }
        return `fallback-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    }
}

// ============================================================
// Status helpers
// ============================================================

function getExportStatus(statusResponse) {
    const body = statusResponse?.data;
    if (body === null || body === undefined) return 'FAILED';
    // Response shape: { code: 200, data: "SUCCESS" | "FAILED" | "EXPORTING" }
    if (body?.code !== 200) return '';
    const raw = body?.data ?? '';
    return String(raw).toUpperCase();
}

// ============================================================
// Export polling queue
//
// Design:
//  - Singleton per tab; cross-tab coordination via BroadcastChannel + Web Locks API.
//  - IndexedDB stores the persistent queue; survives page refresh.
//  - Web Locks elects exactly ONE leader tab that does the polling.
//    When the leader tab closes, the browser automatically releases the lock
//    and the next waiting tab becomes the leader.
//  - Only the tab that called enqueue() has the in-memory callbacks and
//    triggers the file download — other tabs only react to their own items.
// ============================================================

class ExportPollingQueue {
    constructor() {
        this.storage = createExportPollingStorage();
        this.channel = new BroadcastChannel('export-polling-channel');
        this.callbacks = new Map();
        this.isLeader = false;
        this.tabId = getTabId();
        this.loopRunning = false;
        this.pollTimer = null;
        // Serializes concurrent enqueue() calls so IDB reads/writes never interleave.
        this.enqueueChain = Promise.resolve();

        this.ready = this.storage.ready;
        this.channel.addEventListener('message', this.onBroadcast.bind(this));
        this.electLeader();
    }

    // ---- leader election ----

    electLeader() {
        const become = async () => {
            await this.ready;
            this.isLeader = true;
            console.log('[ExportQueue] became leader');
            await this.restoreQueue();
        };

        if (navigator.locks) {
            // navigator.locks guarantees only one tab holds the lock at a time.
            // The promise returned from the callback never resolves, so this tab
            // holds the lock for its entire lifetime; on close the browser releases it.
            navigator.locks.request('export-polling-leader', { mode: 'exclusive' }, async () => {
                await become();
                return new Promise(() => {});
            });
        } else {
            // Fallback for environments without Web Locks (some WebViews).
            become().catch(e => console.error('[ExportQueue] electLeader error', e));
        }
    }

    /** On leader takeover, reset any interrupted items and kick off the queue. */
    async restoreQueue() {
        const items = await this.storage.getAllItems();
        console.log('[ExportQueue] restoreQueue found', items.length, 'items', items.map(i => ({ id: i.downloadId, status: i.status })));
        let hasPending = false;
        for (const item of items) {
            if (item.status === 'processing') {
                await this.storage.patchItem(item.downloadId, { status: 'pending', pollCount: 0 });
            }
            hasPending = true;
        }
        if (hasPending) {
            console.log('[ExportQueue] restoreQueue calling ensureLoopRunning()');
            this.ensureLoopRunning();
        }
    }

    // ---- public API ----

    enqueue(config) {
        // Chain onto the serialization promise so concurrent calls execute one-by-one.
        // The .catch() prevents one failed enqueue from breaking all subsequent ones.
        this.enqueueChain = this.enqueueChain
            .then(() => this._enqueue(config))
            .catch(e => console.error('[ExportQueue] enqueue error', e));
    }

    async _enqueue(config) {
        await this.ready;

        // If this downloadId is already tracked (in-flight or queued in IDB), just
        // refresh the in-memory callback so the correct component receives the result.
        // Do NOT reset the IDB record or add a second timer.
        const existing = await this.storage.getItem(config.downloadId);
        if (existing) {
            this.callbacks.set(config.downloadId, {
                message: config.message,
                onSuccess: config.onSuccess,
                onError: config.onError,
            });
            // Refresh userInfo in IDB so polling and download use up-to-date credentials.
            await this.storage.patchItem(config.downloadId, { userInfo: config.userInfo });
            return;
        }

        // Keep callbacks in-memory only; they cannot be serialized to IndexedDB.
        this.callbacks.set(config.downloadId, {
            message: config.message,
            onSuccess: config.onSuccess,
            onError: config.onError,
        });

        const item = {
            downloadId: config.downloadId,
            fileName: config.fileName,
            fileId: config.fileId,
            userInfo: config.userInfo,
            type: config.type || 'pdf',
            ownerTabId: this.tabId,
            status: 'pending',
            createdAt: Date.now(),
            pollCount: 0,
        };
        await this.storage.putItem(item);

        // Notify other tabs so the leader (if elsewhere) can start polling.
        this.broadcast({ type: 'ITEM_ADDED', item });

        // If this tab is already the leader, ensure the polling loop is running.
        if (this.isLeader) this.ensureLoopRunning();
    }

    // ---- leader: polling loop ----
    // Each round polls every queued item serially, then waits 8 s before the next round.

    ensureLoopRunning() {
        if (this.loopRunning) return;
        this.loopRunning = true;
        this._runLoop().catch(e => console.error('[ExportQueue] loop error', e));
    }

    async _runLoop() {
        // Brief initial delay so the first enqueue call can settle before we start.
        await this._sleep(3000);
        while (true) {
            const all = await this.storage.getAllItems();
            if (!all.length) {
                console.log('[ExportQueue] loop: no items, stopping');
                this.loopRunning = false;
                return;
            }
            all.sort((a, b) => a.createdAt - b.createdAt);
            console.log('[ExportQueue] loop round:', all.map(i => ({ id: i.downloadId, pollCount: i.pollCount })));
            for (const item of all) {
                // Re-fetch in case a previous iteration already finished this item.
                const fresh = await this.storage.getItem(item.downloadId);
                if (!fresh) continue;
                await this._pollOne(fresh);
            }
            await this._sleep(8000);
        }
    }

    async _pollOne(item) {
        // 最多轮询 90 次后视为超时（与原策略一致）
        const MAX_POLLS = 90;
        if (item.pollCount >= MAX_POLLS) {
            await this.finish(item, 'timeout');
            return;
        }
        console.log(`[ExportQueue] _pollOne count=${item.pollCount} id=${item.downloadId} type=${item.type}`);
        let status = '';
        try {
            const res = await getExportStatusAPI(item.downloadId, item.userInfo);
            status = getExportStatus(res);
            console.log('[ExportQueue] _pollOne status response:', status, res?.data);
        } catch (e) {
            console.error('[ExportQueue] _pollOne error', e);
        }
        if (status === 'SUCCESS') {
            await this.finish(item, 'success');
            return;
        }
        if (status === 'FAILED') {
            await this.finish(item, 'failed');
            return;
        }
        await this.storage.patchItem(item.downloadId, { pollCount: item.pollCount + 1, status: 'processing' });
    }

    _sleep(ms) {
        return new Promise(resolve => { this.pollTimer = setTimeout(resolve, ms); });
    }

    async finish(item, result) {
        if (result === 'success') {
            // Broadcast to other tabs BEFORE removing from DB (they may need item data).
            this.broadcast({ type: 'ITEM_COMPLETED', downloadId: item.downloadId, item });
            await this.triggerDownload(item.downloadId, item);
        } else {
            const error = result === 'timeout' ? '轮询超时' : '导出失败';
            this.broadcast({ type: 'ITEM_FAILED', downloadId: item.downloadId, error, item });
            await this.triggerError(item.downloadId, error, item);
        }

        await this.storage.deleteItem(item.downloadId);
    }

    // ---- callback handlers (run in whichever tab owns the callback) ----

    async triggerDownload(downloadId, item) {
        if (item?.ownerTabId !== this.tabId) return;
        const cb = this.callbacks.get(downloadId);
        // Delete synchronously before any await so a concurrent call finds nothing and exits.
        // cb may be absent after a page refresh — the item data from IDB is enough to download.
        this.callbacks.delete(downloadId);
        const noopMessage = { success: () => {}, error: () => {}, closeAll: () => {} };
        const message = cb?.message ?? noopMessage;
        try {
            if (item.type === 'zip') {
                message.closeAll();
                await downloadZipAPI(downloadId, item.userInfo, message);
            } else if (item.type === 'office') {
                await downloadOfficeAPI(downloadId, item.userInfo, () => {}, message, item.fileName);
                message.closeAll();
            } else {
                // PDF: call API which handles blob download internally.
                await downloadPDFAPI(item.fileId, downloadId, item.fileName, item.userInfo, () => {}, message);
                message.closeAll();
            }
            cb?.onSuccess?.();
        } catch (e) {
            console.error('[ExportQueue] download error', e);
        }
    }

    async triggerError(downloadId, error, item) {
        if (item?.ownerTabId !== this.tabId) return;
        const cb = this.callbacks.get(downloadId);
        if (!cb) return;
        cb.message?.closeAll();
        cb.message?.error('文件生成失败');
        cb.onError?.(error);
        this.callbacks.delete(downloadId);
    }

    // ---- cross-tab messaging ----

    onBroadcast(event) {
        const msg = event.data;
        switch (msg.type) {
            case 'ITEM_ADDED':
                // Another tab added an item; ensure the leader's polling loop is running.
                if (this.isLeader) this.ensureLoopRunning();
                break;
            case 'ITEM_COMPLETED':
                // Leader finished; handle callbacks if this tab owns them.
                this.triggerDownload(msg.downloadId, msg.item);
                break;
            case 'ITEM_FAILED':
                this.triggerError(msg.downloadId, msg.error, msg.item);
                break;
        }
    }

    broadcast(msg) {
        this.channel.postMessage(msg);
    }

    // ---- public query ----

    async getActiveTasks() {
        return this.storage.getAllItems();
    }
}

// ============================================================
// Module-level singleton — survives Vue component mount/unmount cycles.
// ============================================================

let _queue = null;

export function getExportPollingQueue() {
    if (!_queue) _queue = new ExportPollingQueue();
    return _queue;
}
